import { ArrowPath, EllipsisHorizontal, Plus, Trash } from "@medusajs/icons"
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Copy,
  DropdownMenu,
  IconButton,
  Input,
  Label,
  Prompt,
  Select,
  StatusBadge,
  Switch,
  Table,
  Text,
  toast,
} from "@medusajs/ui"
import { useEffect, useMemo, useState, type FormEvent } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { AccessEditor, accessSummary, type Access } from "@/components/access-editor"
import { EmptyState, ErrorState, PageHeader, SaveBar, Section } from "@/components/ui"
import {
  useInviteStaff,
  useRemoveStaff,
  useResendInvite,
  useRevokeInvite,
  useSignOutStaff,
  useStaff,
  useStaffMember,
  useTransferOwnership,
  useUpdateStaff,
} from "@/lib/api"
import { formatDate, fullName, relativeTime } from "@/lib/format"
import { useCan, useCurrentMe } from "@/lib/permissions"
import { errorMessage, sdk } from "@/lib/sdk"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { canManageMember, grantable } from "@/lib/staff-rules"
import type { Permission, StaffInvite, StaffMember } from "@/lib/types"

const display = (m: { first_name: string | null; last_name: string | null; email: string }) => fullName(m) || m.email
const initial = (m: { first_name: string | null; email: string }) => (m.first_name?.[0] ?? m.email[0] ?? "?").toUpperCase()

function InviteLinkNotice({ invite, onClose }: { invite: StaffInvite; onClose?: () => void }) {
  if (!invite.invite_url) return null
  return (
    <Alert variant={invite.email_error ? "warning" : "info"} dismissible={!!onClose} className="mb-4 min-w-0 max-w-full overflow-hidden [&>*]:min-w-0">
      <div className="flex min-w-0 max-w-full flex-col gap-y-2">
        {invite.email_error ? (
          <Text size="small">
            The invitation is saved, but the email to {invite.email} couldn't be sent: {invite.email_error} Share this link with them yourself, or use Resend invitation once email works. It
            works once and expires {formatDate(invite.expires_at, false)}.
          </Text>
        ) : (
          <Text size="small">
            Email isn't set up on the server yet, so share this invitation link with {invite.email} yourself. It works once and expires {formatDate(invite.expires_at, false)}.
          </Text>
        )}
        <div className="flex min-w-0 max-w-full items-center gap-x-2 rounded-md border border-ui-border-base bg-ui-bg-field px-2 py-1">
          <code className="txt-compact-xsmall min-w-0 flex-1 truncate" title={invite.invite_url}>{invite.invite_url}</code>
          <span className="shrink-0"><Copy content={invite.invite_url} /></span>
        </div>
      </div>
    </Alert>
  )
}

// ── Security: require two-step verification (owner) ───────────────────────

type StaffSecurity = { require_two_factor: boolean; staff_without_two_factor: number }

function StaffSecuritySection({ isOwner }: { isOwner: boolean }) {
  const qc = useQueryClient()
  const me = useCurrentMe()
  const q = useQuery({ queryKey: ["staff-security"], queryFn: () => sdk.client.fetch<StaffSecurity>("/admin/terify/staff-security") })
  const save = useMutation({
    mutationFn: (on: boolean) => sdk.client.fetch<StaffSecurity>("/admin/terify/staff-security", { method: "POST", body: { require_two_factor: on } }),
    onSuccess: (r) => {
      qc.setQueryData(["staff-security"], r)
      qc.invalidateQueries({ queryKey: ["me"] })
      toast.success(r.require_two_factor ? "Two-step verification is now required" : "Two-step verification is now optional")
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const d = q.data
  const ownOn = !!me.two_factor?.enabled
  return (
    <Section title="Security">
      <div className="flex items-start justify-between gap-4 px-4 py-4">
        <div className="min-w-0">
          <Text size="small" weight="plus">Require two-step verification</Text>
          <Text size="small" className="mt-1 text-ui-fg-subtle">
            Everyone with access to this admin must enter a code from their phone (authenticator app or text message) when they sign in. Staff who haven't set it up are asked to before they can do anything else.
          </Text>
          {d && d.require_two_factor && d.staff_without_two_factor > 0 ? (
            <Text size="xsmall" className="mt-1 text-ui-fg-subtle">{d.staff_without_two_factor === 1 ? "1 account hasn't" : `${d.staff_without_two_factor} accounts haven't`} set it up yet; they'll be asked at their next visit.</Text>
          ) : null}
          {isOwner && !ownOn ? <Text size="xsmall" className="mt-1 text-ui-fg-subtle">Turn on two-step verification for your own account (Settings → Your account) to change this.</Text> : null}
          {!isOwner ? <Text size="xsmall" className="mt-1 text-ui-fg-subtle">Only the store owner can change this.</Text> : null}
        </div>
        <Switch
          aria-label="Require two-step verification"
          checked={!!d?.require_two_factor}
          disabled={!d || !isOwner || !ownOn || save.isPending}
          onCheckedChange={(v) => save.mutate(v)}
        />
      </div>
    </Section>
  )
}

// ── Users and permissions (list) ────────────────────────────────────────────

export function UsersPage() {
  const { data, isLoading, error } = useStaff()
  const me = useCurrentMe()
  const can = useCan()
  const navigate = useNavigate()
  const resend = useResendInvite()
  const revoke = useRevokeInvite()
  const [shared, setShared] = useState<StaffInvite | null>(null)
  const [transferOpen, setTransferOpen] = useState(false)
  const [cancelling, setCancelling] = useState<StaffInvite | null>(null)

  if (error) return <ErrorState error={error} />
  const owner = data?.owner
  const others = (data?.staff ?? []).filter((s) => s.role !== "owner")
  const invites = data?.invites ?? []
  const isOwner = me.staff?.role === "owner"

  const onResend = (inv: StaffInvite) =>
    resend.mutate(inv.id, {
      onSuccess: ({ invite }) => {
        if (invite.invite_url) setShared(invite)
        if (invite.email_error) toast.error(`The invitation email to ${inv.email} couldn't be sent`)
        else if (!invite.invite_url) toast.success(`Invitation sent again to ${inv.email}`)
      },
      onError: (e) => toast.error(errorMessage(e)),
    })

  return (
    <>
      <PageHeader
        title="Users and permissions"
        actions={
          can("staff:write") && (
            <Button size="small" onClick={() => navigate("/settings/users/new")}>
              <Plus /> Add staff
            </Button>
          )
        }
      />
      {shared && <InviteLinkNotice invite={shared} onClose={() => setShared(null)} />}

      <div className="flex flex-col gap-y-4">
        <Section
          title="Store owner"
          actions={
            isOwner &&
            others.some((s) => s.status === "active") && (
              <Button size="small" variant="secondary" onClick={() => setTransferOpen(true)}>
                Transfer ownership
              </Button>
            )
          }
        >
          {owner ? (
            <div className="flex items-center gap-x-3 px-4 py-4">
              <Avatar fallback={initial(owner)} src={owner.avatar_url ?? undefined} />
              <div className="min-w-0 flex-1">
                <Text size="small" weight="plus">
                  {display(owner)} {owner.id === me.staff?.id && <span className="text-ui-fg-muted">(you)</span>}
                </Text>
                <Text size="small" className="text-ui-fg-subtle">
                  {owner.email} · Last active {relativeTime(owner.last_seen_at)}
                </Text>
              </div>
            </div>
          ) : (
            <EmptyState title={isLoading ? "Loading…" : "No store owner"} />
          )}
        </Section>

        <StaffSecuritySection isOwner={isOwner} />

        <Section title={`Staff (${others.length + invites.length})`}>
          {!isLoading && others.length === 0 && invites.length === 0 ? (
            <EmptyState
              title="No staff yet"
              body="Add staff to give them their own sign-in and only the access they need."
              action={
                can("staff:write") && (
                  <Button size="small" variant="secondary" onClick={() => navigate("/settings/users/new")}>
                    Add staff
                  </Button>
                )
              }
            />
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Permissions</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell className="whitespace-nowrap">Last active</Table.HeaderCell>
                  <Table.HeaderCell className="w-10" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {others.map((s) => (
                  <Table.Row key={s.id} className="cursor-pointer" onClick={() => navigate(`/settings/users/${s.id}`)}>
                    <Table.Cell className="w-full max-w-0">
                      <div className="flex min-w-[200px] items-center gap-x-3 py-1">
                        <Avatar size="small" fallback={initial(s)} src={s.avatar_url ?? undefined} />
                        <div className="min-w-0">
                          <Text size="small" weight="plus" className="truncate">
                            {display(s)} {s.id === me.staff?.id && <span className="text-ui-fg-muted">(you)</span>}
                          </Text>
                          <Text size="xsmall" className="truncate text-ui-fg-subtle" title={s.email}>
                            {s.email}
                          </Text>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="small" className="whitespace-nowrap">{accessSummary(s.role, s.granted_permissions)}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        {s.status === "active" ? <StatusBadge color="green">Active</StatusBadge> : <StatusBadge color="grey">Deactivated</StatusBadge>}
                        {s.status === "active" && !(s as any).two_factor ? <Badge size="2xsmall" color="orange" title="Two-step verification isn't set up yet">No 2-step</Badge> : null}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="small" className="whitespace-nowrap text-ui-fg-subtle">
                        {relativeTime(s.last_seen_at)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell />
                  </Table.Row>
                ))}
                {invites.map((inv) => (
                  <Table.Row key={inv.id}>
                    <Table.Cell className="w-full max-w-0">
                      <div className="flex min-w-[200px] items-center gap-x-3 py-1">
                        <Avatar size="small" variant="squared" fallback={(inv.first_name?.[0] ?? inv.email[0] ?? "?").toUpperCase()} />
                        <div className="min-w-0">
                          <Text size="small" weight="plus" className="truncate">
                            {fullName(inv) || inv.email}
                          </Text>
                          <Text size="xsmall" className="truncate text-ui-fg-subtle" title={`${inv.email} · invited by ${inv.invited_by_email ?? "—"}`}>
                            {inv.email} · invited by {inv.invited_by_email ?? "—"}
                          </Text>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="small" className="whitespace-nowrap">{accessSummary(inv.role, inv.granted_permissions)}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      {inv.status === "expired" ? <StatusBadge color="red">Invitation expired</StatusBadge> : <StatusBadge color="orange">Invited</StatusBadge>}
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="small" className="whitespace-nowrap text-ui-fg-subtle">
                        {inv.status === "expired" ? "—" : `Expires ${formatDate(inv.expires_at, false)}`}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      {can("staff:write") && (inv.role === "staff" || isOwner) && (
                        <DropdownMenu>
                          <DropdownMenu.Trigger asChild>
                            <IconButton size="small" variant="transparent" aria-label="Invitation actions">
                              <EllipsisHorizontal />
                            </IconButton>
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Content align="end">
                            <DropdownMenu.Item className="gap-x-2" onClick={() => onResend(inv)}>
                              <ArrowPath /> Resend invitation
                            </DropdownMenu.Item>
                            <DropdownMenu.Separator />
                            <DropdownMenu.Item className="gap-x-2 text-ui-fg-error" onClick={() => setCancelling(inv)}>
                              <Trash /> Cancel invitation
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            </div>
          )}
        </Section>

        <Text size="xsmall" className="px-1 text-ui-fg-muted">
          Every change staff make is recorded in the {can("logs:read") ? <Link to="/settings/activity" className="text-ui-fg-interactive">activity log</Link> : "activity log (the store owner can view it)"}.
        </Text>
      </div>

      <Prompt open={!!cancelling} onOpenChange={(o) => !o && setCancelling(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Cancel invitation?</Prompt.Title>
            <Prompt.Description>The link sent to {cancelling?.email} will stop working. You can invite them again later.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep</Prompt.Cancel>
            <Prompt.Action
              onClick={() =>
                cancelling &&
                revoke.mutate(cancelling.id, {
                  onSuccess: () => toast.success("Invitation cancelled"),
                  onError: (e) => toast.error(errorMessage(e)),
                  onSettled: () => setCancelling(null),
                })
              }
            >
              Cancel invitation
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>

      {transferOpen && <TransferOwnership candidates={others.filter((s) => s.status === "active")} onClose={() => setTransferOpen(false)} />}
    </>
  )
}

// ── Transfer ownership ──────────────────────────────────────────────────────

function TransferOwnership({ candidates, onClose }: { candidates: StaffMember[]; onClose: () => void }) {
  const [staffId, setStaffId] = useState("")
  const [password, setPassword] = useState("")
  const transfer = useTransferOwnership()
  const target = candidates.find((c) => c.id === staffId)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    transfer.mutate(
      { staff_id: staffId, password },
      {
        onSuccess: () => {
          toast.success(`${target ? display(target) : "They"} now own the store`)
          onClose()
        },
      }
    )
  }

  return (
    <Prompt open onOpenChange={(o) => !o && onClose()}>
      <Prompt.Content>
        <form onSubmit={submit}>
          <Prompt.Header>
            <Prompt.Title>Transfer ownership</Prompt.Title>
            <Prompt.Description>
              The new owner gets full control, including staff and ownership. You keep full permissions as staff, and they can change that.
            </Prompt.Description>
          </Prompt.Header>
          <div className="flex flex-col gap-y-4 px-4 pb-4 pt-4">
            <div className="flex flex-col gap-y-1.5">
              <Label>New store owner</Label>
              <Select value={staffId} onValueChange={setStaffId}>
                <Select.Trigger>
                  <Select.Value placeholder="Choose a staff member" />
                </Select.Trigger>
                <Select.Content>
                  {candidates.map((c) => (
                    <Select.Item key={c.id} value={c.id}>
                      {display(c)} ({c.email})
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
            </div>
            <div className="flex flex-col gap-y-1.5">
              <Label htmlFor="transfer-password">Your password</Label>
              <Input id="transfer-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {transfer.error && <Text size="small" className="text-ui-fg-error">{errorMessage(transfer.error)}</Text>}
          </div>
          <Prompt.Footer>
            <Prompt.Cancel type="button">Cancel</Prompt.Cancel>
            <Button type="submit" size="small" variant="danger" disabled={!staffId || !password} isLoading={transfer.isPending}>
              Transfer ownership
            </Button>
          </Prompt.Footer>
        </form>
      </Prompt.Content>
    </Prompt>
  )
}

// ── Add staff ───────────────────────────────────────────────────────────────

export function AddStaffPage() {
  const me = useCurrentMe()
  const navigate = useNavigate()
  const invite = useInviteStaff()
  const g = grantable(me)
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [access, setAccess] = useState<Access>({ role: "staff", permissions: [] })
  const [sent, setSent] = useState<StaffInvite | null>(null)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    invite.mutate(
      { email, first_name: firstName || undefined, last_name: lastName || undefined, role: access.role, permissions: access.permissions },
      {
        onSuccess: ({ invite }) => {
          if (invite.invite_url) setSent(invite)
          else {
            toast.success(`Invitation sent to ${invite.email}`)
            navigate("/settings/users")
          }
        },
      }
    )
  }

  if (sent) {
    return (
      <>
        <PageHeader title={sent.email_error ? "Invitation saved, email not sent" : "Invitation created"} back="/settings/users" />
        <InviteLinkNotice invite={sent} />
        <Button size="small" variant="secondary" onClick={() => navigate("/settings/users")}>
          Back to users and permissions
        </Button>
      </>
    )
  }

  return (
    <form onSubmit={submit}>
      <PageHeader title="Add staff" subtitle="They'll get an email to create their account. Invitations expire after 7 days." back="/settings/users" />
      <div className="flex flex-col gap-y-4 pb-24">
        <Section title="Staff details">
          <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-2">
            <div className="flex flex-col gap-y-1.5">
              <Label htmlFor="first">First name</Label>
              <Input id="first" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="off" />
            </div>
            <div className="flex flex-col gap-y-1.5">
              <Label htmlFor="last">Last name</Label>
              <Input id="last" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="off" />
            </div>
            <div className="flex flex-col gap-y-1.5 md:col-span-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
            </div>
          </div>
        </Section>
        <Section title="Permissions">
          <div className="px-4 py-4">
            <AccessEditor value={access} onChange={setAccess} canGiveFull={g.full} grantable={g.permissions} />
          </div>
        </Section>
      </div>
      <SaveBar
        visible
        message={invite.error ? errorMessage(invite.error) : "New staff member"}
        error={!!invite.error}
        onDiscard={() => navigate("/settings/users")}
        discardLabel="Cancel"
        saveLabel="Send invitation"
        saving={invite.isPending}
        disabled={!email || (access.role === "staff" && access.permissions.length === 0)}
      />
    </form>
  )
}

// ── Staff member ────────────────────────────────────────────────────────────

const sameSet = (a: Permission[], b: Permission[]) => a.length === b.length && a.every((x) => b.includes(x))

export function StaffMemberPage() {
  const { id = "" } = useParams()
  const me = useCurrentMe()
  const navigate = useNavigate()
  const { data, isLoading, error } = useStaffMember(id)
  const update = useUpdateStaff(id)
  const signOut = useSignOutStaff(id)
  const qc = useQueryClient()
  const remove = useRemoveStaff(id)
  const g = grantable(me)
  const member = data?.staff
  const initialAccess = useMemo<Access | null>(
    () => (member && member.role !== "owner" ? { role: member.role, permissions: member.role === "staff" ? member.permissions : [] } : null),
    [member]
  )
  const [access, setAccess] = useState<Access | null>(initialAccess)
  const [confirm, setConfirm] = useState<null | "deactivate" | "remove" | "signout">(null)
  useEffect(() => setAccess(initialAccess), [initialAccess])

  if (error) return <ErrorState error={error} />
  if (isLoading || !member) return <EmptyState title="Loading…" />

  const manage = canManageMember(me, member)
  const dirty = !!access && !!initialAccess && (access.role !== initialAccess.role || !sameSet(access.permissions, initialAccess.permissions))

  const save = (e: FormEvent) => {
    e.preventDefault()
    if (!access) return
    update.mutate(
      { role: access.role, permissions: access.role === "staff" ? access.permissions : [] },
      { onSuccess: () => toast.success("Permissions saved") }
    )
  }

  const run = () => {
    const done = () => setConfirm(null)
    if (confirm === "signout")
      signOut.mutate(undefined, { onSuccess: () => toast.success(`${display(member)} was signed out everywhere`), onError: (e) => toast.error(errorMessage(e)), onSettled: done })
    if (confirm === "deactivate") {
      const next = member.status !== "active"
      update.mutate(
        { active: next },
        { onSuccess: () => toast.success(next ? "Account reactivated" : "Account deactivated"), onError: (e) => toast.error(errorMessage(e)), onSettled: done }
      )
    }
    if (confirm === "remove")
      remove.mutate(undefined, {
        onSuccess: () => {
          toast.success(`${display(member)} was removed`)
          navigate("/settings/users")
        },
        onError: (e) => toast.error(errorMessage(e)),
        onSettled: done,
      })
  }

  return (
    <form onSubmit={save}>
      <PageHeader
        title={
          <span className="flex items-center gap-x-2">
            {display(member)}
            {member.role === "owner" && <Badge size="small">Store owner</Badge>}
            {member.status === "deactivated" && <StatusBadge color="grey">Deactivated</StatusBadge>}
          </span>
        }
        subtitle={member.email}
        back="/settings/users"
      />

      {!manage.ok && (
        <Alert variant="info" className="mb-4">
          {manage.reason}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 pb-24 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-y-4">
          <Section title="Permissions">
            <div className="px-4 py-4">
              {member.role === "owner" ? (
                <Text size="small" className="text-ui-fg-subtle">
                  The store owner has every permission. Ownership can be transferred from Users and permissions.
                </Text>
              ) : (
                access && (
                  <AccessEditor
                    value={access}
                    onChange={setAccess}
                    canGiveFull={g.full}
                    grantable={g.permissions}
                    disabled={!manage.ok}
                  />
                )
              )}
            </div>
          </Section>

          <Section title="Recent sign-ins">
            {data.logins.length === 0 ? (
              <EmptyState title="No sign-ins yet" />
            ) : (
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Date</Table.HeaderCell>
                    <Table.HeaderCell>IP address</Table.HeaderCell>
                    <Table.HeaderCell>Device</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {data.logins.map((l, i) => (
                    <Table.Row key={i}>
                      <Table.Cell>{formatDate(l.at)}</Table.Cell>
                      <Table.Cell>
                        {l.ip ?? "—"}
                        {l.country ? ` · ${l.country}` : ""}
                      </Table.Cell>
                      <Table.Cell className="max-w-[260px] truncate text-ui-fg-subtle">{deviceOf(l.user_agent)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </Section>
        </div>

        <div className="flex flex-col gap-y-4">
          <Section title="Details">
            <dl className="flex flex-col gap-y-3 px-4 py-4">
              <Row label="Email" value={member.email} />
              <Row label="Staff since" value={formatDate(member.created_at, false)} />
              <Row label="Last active" value={relativeTime(member.last_seen_at)} />
              <Row label="Two-step verification" value={(member as any).two_factor ? "On" : "Off"} />
            </dl>
          </Section>

          {manage.ok && (
            <Section title="Account access">
              <div className="flex flex-col gap-y-3 px-4 py-4">
                <AccessAction
                  title="Sign out of all sessions"
                  body="Ends every session on every device. They can sign in again."
                  label="Sign out"
                  onClick={() => setConfirm("signout")}
                />
                <AccessAction
                  title={member.status === "active" ? "Deactivate account" : "Reactivate account"}
                  body={
                    member.status === "active"
                      ? "Blocks sign-in but keeps the account and history, so you can restore access later."
                      : "Lets them sign in again with the same permissions."
                  }
                  label={member.status === "active" ? "Deactivate" : "Reactivate"}
                  onClick={() => setConfirm("deactivate")}
                />
                {(member as any).two_factor && (
                  <AccessAction
                    title="Reset two-step verification"
                    body="For a lost phone: turns it off so they can sign in with their password and set it up again."
                    label="Reset"
                    onClick={() =>
                      sdk.client
                        .fetch(`/admin/terify/staff/${member.id}/2fa`, { method: "DELETE" })
                        .then(() => (toast.success("Two-step verification reset"), qc.invalidateQueries({ queryKey: ["staff"] })))
                        .catch((e) => toast.error(errorMessage(e)))
                    }
                  />
                )}
                <AccessAction title="Remove staff" body="Deletes their account. Their past actions stay in the activity log." label="Remove" danger onClick={() => setConfirm("remove")} />
              </div>
            </Section>
          )}
        </div>
      </div>

      <SaveBar
        visible={dirty}
        message={update.error ? errorMessage(update.error) : "Unsaved changes"}
        error={!!update.error}
        onDiscard={() => setAccess(initialAccess)}
        saving={update.isPending}
        disabled={access?.role === "staff" && access.permissions.length === 0}
      />

      <Prompt open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>
              {confirm === "remove" ? `Remove ${display(member)}?` : confirm === "signout" ? `Sign ${display(member)} out everywhere?` : member.status === "active" ? `Deactivate ${display(member)}?` : `Reactivate ${display(member)}?`}
            </Prompt.Title>
            <Prompt.Description>
              {confirm === "remove"
                ? "Their account is deleted and they can't sign in. This can't be undone, but you can invite them again."
                : confirm === "signout"
                  ? "They'll need to sign in again on every device."
                  : member.status === "active"
                    ? "They're signed out right away and can't sign in until you reactivate the account."
                    : "They can sign in again with their previous permissions."}
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action onClick={run}>{confirm === "remove" ? "Remove" : confirm === "signout" ? "Sign out" : member.status === "active" ? "Deactivate" : "Reactivate"}</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </form>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-x-4">
      <Text size="small" className="text-ui-fg-subtle">
        {label}
      </Text>
      <Text size="small" className="truncate text-right">
        {value}
      </Text>
    </div>
  )
}

function AccessAction({ title, body, label, onClick, danger }: { title: string; body: string; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <div className="flex flex-col items-start gap-y-2 border-b border-ui-border-base pb-3 last:border-0 last:pb-0">
      <div className="flex flex-col">
        <Text size="small" weight="plus">
          {title}
        </Text>
        <Text size="xsmall" className="text-ui-fg-subtle">
          {body}
        </Text>
      </div>
      <Button type="button" size="small" variant={danger ? "danger" : "secondary"} onClick={onClick} className="whitespace-nowrap">
        {label}
      </Button>
    </div>
  )
}

function deviceOf(ua: string | null): string {
  if (!ua) return "—"
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser"
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : ""
  return os ? `${browser} on ${os}` : browser
}

/** Kept for links that still point at the old Staff page. */
export function LegacyStaffRedirect() {
  const navigate = useNavigate()
  useEffect(() => {
    navigate("/settings/users", { replace: true })
  }, [navigate])
  return null
}

